import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, ArrowRight, Clock } from 'lucide-react';

// Section UI strings (post content comes from the DB-backed blog API)
const ui = {
  tr: { blogTitle: 'Blog & İçerik', blogSubtitle: 'Geri bildirimi etkili yönetmek için ipuçları, stratejiler ve en iyi uygulamalar', readMore: 'Devamını Oku', viewAll: 'Tümünü Gör', min: 'dk' },
  en: { blogTitle: 'Blog & Insights', blogSubtitle: 'Tips, strategies and best practices to manage customer feedback effectively', readMore: 'Read More', viewAll: 'View All', min: 'min' },
  de: { blogTitle: 'Blog & Wissen', blogSubtitle: 'Tipps, Strategien und Best Practices für effektives Feedback-Management', readMore: 'Weiterlesen', viewAll: 'Alle ansehen', min: 'Min.' },
  es: { blogTitle: 'Blog & Contenido', blogSubtitle: 'Consejos, estrategias y buenas prácticas para gestionar el feedback con eficacia', readMore: 'Leer más', viewAll: 'Ver todo', min: 'min' },
  fr: { blogTitle: 'Blog & Ressources', blogSubtitle: 'Conseils, stratégies et bonnes pratiques pour gérer efficacement les retours', readMore: 'Lire la suite', viewAll: 'Tout voir', min: 'min' },
};

const BlogSection = () => {
  const { language } = useLanguage();
  const t = ui[language] || ui.en;
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(`/api/blog?lang=${encodeURIComponent(language)}`)
      .then((r) => r.json())
      .then((d) => { if (active) { setPosts((d && d.data) || []); setLoaded(true); } })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [language]);

  // Hide the section entirely until we know there is content
  if (loaded && posts.length === 0) return null;

  const top = posts.slice(0, 6);

  return (
    <section data-blog-section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-5">{t.blogTitle}</h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2">{t.blogSubtitle}</p>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {top.map((post, index) => (
            <a
              key={post.slug}
              href={`/blog/${post.slug}?lang=${language}`}
              className="group h-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-lg dark:hover:shadow-gray-900/30 transition-all duration-300 hover:-translate-y-1 flex flex-col no-underline"
              style={{ animation: `fadeInUp 0.6s ease-out ${index * 100}ms both` }}
            >
              {post.cover && (
                <div className="relative w-full h-48 sm:h-56 overflow-hidden bg-gray-200 dark:bg-gray-700">
                  <img src={post.cover} alt={post.coverAlt || post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              )}

              <div className="flex-1 px-6 sm:px-8 py-5 sm:py-6 flex flex-col">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">{post.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 line-clamp-3 flex-1">{post.excerpt}</p>
                <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {post.date && (<div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>{post.date}</span></div>)}
                  {post.readingTime && (<div className="flex items-center gap-1.5"><Clock className="w-4 h-4" /><span>{post.readingTime} {t.min}</span></div>)}
                </div>
                <span className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 rounded-lg group-hover:bg-cyan-600 group-hover:text-white dark:group-hover:bg-cyan-600 dark:group-hover:text-white transition-all duration-300">
                  {t.readMore}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </a>
          ))}
        </div>

        {/* View All button */}
        <div className="text-center mt-10 sm:mt-12">
          <a
            href={`/blog?lang=${language}`}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white text-base font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 no-underline"
          >
            {t.viewAll}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </section>
  );
};

export default BlogSection;
